// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import CardComponent from "@store/Library/Widgets/Card/Card";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import CardFooterComponent from "@store/Library/Widgets/Card/CardFooter";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import useBackgroundSkeleton from "@store/Framework/Common/Hooks/useBackgroundSkeleton";
import { lazyLoadStyles } from "@store/Themes/common/mixins/lazyload";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

interface BunzlCategoryCardStyles {
    cardStyles?: InjectableCss;
    cardBodyStyles?: InjectableCss;
    cardImageStyles?: InjectableCss;
    cardFooterStyles?: InjectableCss;
    linkStyles: LinkPresentationProps;
    titleStyles?: TypographyPresentationProps;
    cardClickableStyles?: LinkPresentationProps;
}
const getLinkStyles = (styles: LinkPresentationProps, linkText?: string): LinkPresentationProps => {
    if (!linkText) {
        return {
            ...styles,
            css: css`
                ${styles.css};
                max-width: 100%;
            `,
            icon: undefined,
        };
    }
    return {
        ...styles,
        css: css`
            ${styles.css};
        `,
    };
};
const getCardImageStyles = (
    imageSrc: string,
    enableSkeleton: boolean,
    bgSkeletonCss: ReturnType<typeof useBackgroundSkeleton>["css"],
    styles?: InjectableCss,
) => {
    return {
        css: css`
            ${styles?.css}
            ${enableSkeleton ? bgSkeletonCss : `background-image: url(${imageSrc});`}
        `,
    };
};

const getCardFooterStyles = (styles?: InjectableCss, linkText?: string) => {
    return {
        css: css`
            ${styles?.css}
            ${!linkText && "justify-content: center;"}
        `,
    };
};

interface BunzlCategoryCardProps {
    category?: Category;
    linkText?: string;
    enableSkeleton?: boolean;
    extendedStyles?: BunzlCategoryCardStyles;
}

const bunzlCategoryCardStyles: BunzlCategoryCardStyles = {
    cardClickableStyles: {
        css: css`
            height: 100%;
        `,
    },
    linkStyles: {
        icon: {
            position: "right",
            iconProps: {
                src: ChevronRight,
                color: "text.link",
                size: 18,
                css: css`
                    margin-left: ${pxToRem(4)};
                    pointer-events: none;
                `,
            },
        },
        mergeCss: true,
        css: css`
            :hover {
                svg {
                    color: ${getColor("text.linkHover")};
                }
            }
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    titleStyles: {
        css: css`
            margin-bottom: 0;
            // design has an 18px font size, but the style guide has a 16px font size
            font-size: ${pxToRem(18)};
            max-width: 65%;
        `,
    },
    cardStyles: {
        css: css`
            width: 100%;
            :hover {
                box-shadow: ${({ theme }) => theme.shadows.small};
            }
            height: 100%;
            ${breakpointValues([])}
            background-color: ${getColor("common.shadesLight")};
            border-radius: ${({ theme }) => theme.design.radius.medium};
            padding: 0;
        `,
    },

    cardImageStyles: {
        css: css`
            max-width: 100%;
            height: 100%;
            background-position: center;
            mix-blend-mode: multiply;
            background-size: contain;
            background-repeat: no-repeat;
        `,
    },
    cardBodyStyles: {
        css: css`
            width: 100%;
            height: 100%;
            aspect-ratio: 1;
            padding: clamp(0px, (100% - 275px) * 999, ${pxToRem(40)});
        `,
    },

    cardFooterStyles: {
        css: css`
            width: 100%;

            gap: ${pxToRem(16)};
            height: 100%;
            justify-content: space-between;
            padding: ${pxToRem(16)};
            padding-top: ${pxToRem(12)};
            align-items: flex-end;
            flex-wrap: wrap;
            container-type: inline-size;
        `,
    },
};

const loadingCardStyles: InjectableCss = {
    css: css`
        ${bunzlCategoryCardStyles.cardStyles?.css}
        ${lazyLoadStyles()}
    `,
};

const BunzlCategoryCard: React.FC<BunzlCategoryCardProps> = ({
    category,
    linkText,
    enableSkeleton = true,
    extendedStyles,
}) => {
    const styles = useMergeProps(bunzlCategoryCardStyles, extendedStyles);

    const { css: bgSkeletonCss } = useBackgroundSkeleton(
        enableSkeleton && category?.smallImagePath ? category.smallImagePath : undefined,
    );
    const imageStyles = category
        ? getCardImageStyles(category.smallImagePath, enableSkeleton, bgSkeletonCss, styles.cardImageStyles)
        : undefined;
    const footerStyles = getCardFooterStyles(styles.cardFooterStyles, linkText);
    const linkStyles = getLinkStyles(styles.linkStyles, linkText);

    return (
        <CardComponent
            className="category-card"
            href={category?.path}
            variant="unstyled"
            extendedStyles={category ? styles.cardStyles : loadingCardStyles}
        >
            <CardBodyComponent className="category-card-body" extendedStyles={styles.cardBodyStyles}>
                {imageStyles && <StyledWrapper {...imageStyles} className="category-card-image" />}
            </CardBodyComponent>
            <CardFooterComponent className="category-card-footer" extendedStyles={footerStyles}>
                {linkText && (
                    <Typography
                        className="category-card-product-title"
                        variant="h5"
                        forwardAs="p"
                        {...styles.titleStyles}
                    >
                        {category?.shortDescription}
                    </Typography>
                )}
                <Link className="category-card-link" {...linkStyles}>
                    {linkText || category?.shortDescription}
                </Link>
            </CardFooterComponent>
        </CardComponent>
    );
};

export default BunzlCategoryCard;
